import Clickable from 'components/Common/Clickable'
import Pane from 'components/Common/Pane'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import RadioButton from 'LuxKit/Radio/RadioButton'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import { rem } from 'polished'
import { useCallback } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import noop from 'lib/function/noop'
import { useIntl } from 'react-intl'
import { getIllustrativePurposesOnlyLabel } from 'lib/flights/sharedFlightLabels'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import CharterRewardGreatValueBadge from './CharterRewardGreatValueBadge'

const CardClickable = styled(Clickable)`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

  &:focus-visible {
    outline: ${rem(2)} solid ${props => props.theme.palette.highlight.primary.normalForeground};
    outline-offset: ${rem(2)};
    border-radius: ${props => props.theme.borderRadius.S};
  }
`

const CardShell = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 0;
`

// Keeps cabin image tops aligned across cash and reward cards regardless of header content.
const HeaderSlot = styled.div`
  min-height: ${rem(100)};
`

const FullHeightPane = styled(Pane)`
  height: 100%;
  width: 100%;
  min-width: ${rem(260)};
`

const DisclaimerLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.six};
`

const DisabledMessageBanner = styled.div`
  display: flex;
  align-items: center;
  padding: ${rem(8)} ${rem(12)};
  background-color: ${props => props.theme.palette.messaging.warning.lightBackground};
  border-radius: ${props => props.theme.borderRadius.S};
`

const SubtitleRow = styled.div`
  display: flex;
  align-items: center;
  min-height: ${rem(20)};
`

interface Props {
  fareId: string
  isSelected: boolean
  name: string
  subtitle: React.ReactNode
  imageId: string
  onSelect: (fareId: string) => void
  disabled?: boolean
  disabledMessage?: string
  /** When true, renders the floating "Great value" badge + Société Rewards subline. */
  rewardHeadingStyle?: boolean
}

function FareClassCard({
  fareId,
  isSelected,
  name,
  subtitle,
  imageId,
  onSelect,
  disabled,
  disabledMessage,
  rewardHeadingStyle,
  children,
}: React.PropsWithChildren<Props>) {
  const intl = useIntl()
  const illustrativePurposesOnlyLabel = getIllustrativePurposesOnlyLabel(intl)
  const onClick = useCallback(() => {
    if (!disabled) {
      onSelect(fareId)
    }
  }, [fareId, onSelect, disabled])

  const societeRewardsSubline = intl.formatMessage(
    {
      id: 'oDGwSeNRaY8VwqgIVTRP',
      defaultMessage: '{loyaltyProgramName} Rewards',
      description: 'Inline label on Maldives charter reward fare cards naming the loyalty program',
    },
    { loyaltyProgramName: getLoyaltyProgramDisplayName() },
  )

  const headerRow = rewardHeadingStyle
    ? (
        <VerticalSpacer gap={4}>
          <Group direction="horizontal" gap={8} horizontalAlign="space-between" verticalAlign="start">
            <Heading variant="heading6" colour="neutral-one">{name}</Heading>
            <RadioButton checked={isSelected} disabled={disabled} />
          </Group>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <ClassicRewardsSealIcon size={20} colour="highlight-primary-dark" aria-hidden />
            <BodyText variant="small" weight="bold" colour="highlight-primary-dark">
              {societeRewardsSubline}
            </BodyText>
          </Group>
          <SubtitleRow>{!disabled && subtitle}</SubtitleRow>
        </VerticalSpacer>
      )
    : (
        <Group direction="horizontal" gap={8} horizontalAlign="space-between" verticalAlign="start">
          <VerticalSpacer gap={4}>
            <Heading variant="heading6">{name}</Heading>
            <SubtitleRow>{!disabled && subtitle}</SubtitleRow>
          </VerticalSpacer>
          <RadioButton checked={isSelected} disabled={disabled} />
        </Group>
      )

  return <CardClickable
    onClick={disabled ? noop : onClick}
    aria-pressed={isSelected}
    aria-disabled={disabled || undefined}
  >
    <CardShell>
      {rewardHeadingStyle && <CharterRewardGreatValueBadge />}
      <FullHeightPane
        border={isSelected ? 'neutral-one' : 'neutral-five'}
        padding="32 16 24 16"
        background={disabled ? 'neutral-seven' : 'neutral-eight'}
      >
        <VerticalSpacer gap={16}>
          <VerticalSpacer gap={8}>
            <HeaderSlot>{headerRow}</HeaderSlot>
            <div>
              <ResponsiveImage
                id={imageId}
                aspectRatio="218:143"
                desktopAspectRatio="309:174"
              />
              <DisclaimerLabel>
                <Caption variant="medium" colour="neutral-two">
                  {illustrativePurposesOnlyLabel}
                </Caption>
              </DisclaimerLabel>
            </div>
          </VerticalSpacer>
          {disabledMessage && <DisabledMessageBanner>
            <BodyText variant="small" colour="warning-light-contrast">{disabledMessage}</BodyText>
          </DisabledMessageBanner>}
          <div>
            {children}
          </div>
        </VerticalSpacer>
      </FullHeightPane>
    </CardShell>
  </CardClickable>
}

export default FareClassCard
